import React, { useEffect, useRef, useState } from 'react';
import { Flex, Tour, type TourProps } from 'antd';
import { Echo, type EchoMood } from '../components/Echo.js';
import type { OptionsTab } from './tabs.js';
import { findTourTarget, type TourTargetName } from './tourTargets.js';

/** One stop of the tour: what Echo says, how he looks, and what he points at. */
interface Stop {
  mood: EchoMood;
  title: string;
  says: string;
  /** The tab that must be open for this stop (null: the top bar, any tab will do). */
  tab: OptionsTab | null;
  /** The part of the page Echo points at; without one he stands in the middle. */
  target?: TourTargetName;
}

const STOPS: Stop[] = [
  {
    mood: 'hello',
    title: 'Hi, I’m Echo!',
    says: 'I’m a bat. Bats find their way by sending out a ping and listening for the echo. I do the same with German words: I ping you, and you remember. Come on, I’ll show you around!',
    tab: 'dashboard',
  },
  {
    mood: 'point',
    title: 'Give me any German word',
    says: 'Type one here and I make the card for you: meaning, article, example and a picture.',
    tab: null,
    target: 'add-word',
  },
  {
    mood: 'idea',
    title: 'Or catch words while you browse',
    says: 'In Chrome, select a word on any page, right-click and choose “Add to LingoPing”. The LingoPing icon in your toolbar works too.',
    tab: null,
    target: 'add-word',
  },
  {
    mood: 'learning',
    title: 'Decks are word lists',
    says: 'These are ready-made for you. Open one to see its words.',
    tab: 'decks',
    target: 'public-decks',
  },
  {
    mood: 'point',
    title: 'Follow one deck',
    says: 'Then, whenever you have nothing else to learn, I offer you its next word. You tell me: learn it, I know it, or not now.',
    tab: 'decks',
    target: 'follow',
  },
  {
    mood: 'idea',
    title: 'Bring your own words',
    says: 'Give me a word list or the subtitles of a film, and I turn it into a deck of your own.',
    tab: 'decks',
    target: 'import',
  },
  {
    mood: 'thinking',
    title: 'What’s coming up',
    says: 'These words are waiting to be learned. The ones you add yourself always go first.',
    tab: 'dashboard',
    target: 'up-next',
  },
  {
    mood: 'wink',
    title: 'Words you already know',
    says: 'Tell me, and I never show them again. Add one here, or choose “I know it” on any word.',
    tab: 'known',
    target: 'known-add',
  },
  {
    mood: 'learning',
    title: 'All your cards',
    says: 'Open a card to see its example, or to write yourself a note.',
    tab: 'cards',
    target: 'cards',
  },
  {
    mood: 'ping',
    title: 'How often I ping you',
    says: 'Pick how often I remind you to review. Little and often works best.',
    tab: 'settings',
    target: 'remind',
  },
  {
    mood: 'point',
    title: 'Short reviews',
    says: 'And how many cards each time. Five takes about a minute.',
    tab: 'settings',
    target: 'per-review',
  },
  {
    mood: 'thinking',
    title: 'New words per day',
    says: 'The most new words I start with you in a day, so it never gets too much.',
    tab: 'settings',
    target: 'new-words',
  },
  {
    mood: 'celebrate',
    title: 'That’s it!',
    says: 'Reviews happen in the LingoPing extension. I’ll ping you there when your cards are due. Viel Spaß!',
    tab: 'dashboard',
    target: 'review',
  },
];

/** How long a stop waits for its tab to draw the part it points at. */
const TARGET_WAIT_MS = 1500;

/**
 * Echo and what he says, as the whole of a tour step. The headline sits right on top of the text,
 * beside Echo, so the two read as one thing he says. He hops in on every new step.
 */
const EchoSays: React.FC<{ mood: EchoMood; title: string; says: string }> = ({
  mood,
  title,
  says,
}) => (
  // The step has no headline of its own, so this adds the space the headline row used to give.
  <Flex gap={16} align="center" style={{ padding: '20px 4px 10px' }}>
    <Echo mood={mood} size={76} style={{ animation: 'lingoping-echo-hop 0.5s ease-out' }} />
    {/* Room on the right for the tour's close button. */}
    <div style={{ fontSize: 14, lineHeight: 1.6, paddingRight: 24 }}>
      <div
        data-tour-title
        style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3, marginBottom: 6 }}
      >
        {title}
      </div>
      {says}
    </div>
  </Flex>
);

const HOP = `
@keyframes lingoping-echo-hop {
  0% { transform: translateY(8px) scale(0.92); opacity: 0; }
  60% { transform: translateY(-4px) scale(1.03); opacity: 1; }
  100% { transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  [style*="lingoping-echo-hop"] { animation: none !important; }
}`;

/**
 * Echo's tour of the dashboard. He introduces himself, then points at one thing at a time, in the
 * order a new learner needs it, opening each tab before he points into it. It starts by itself
 * once, right after the first-run flow, and again whenever the learner asks ("Tour" in the top
 * bar). `hasReviewButton`: the website has a "Review in the extension" button for the last stop to
 * point at; elsewhere Echo says it from the middle of the page.
 */
interface DashboardTourProps {
  onClose: () => void;
  hasReviewButton: boolean;
  /** Opens a tab, so the part a stop points at is on the page. */
  onShowTab?: (tab: OptionsTab) => void;
}

export const DashboardTour: React.FC<DashboardTourProps & { open: boolean }> = ({
  open,
  ...rest
}) =>
  // Mounted afresh for every tour, so each one starts with Echo's hello.
  open ? <RunningTour {...rest} /> : null;

const RunningTour: React.FC<DashboardTourProps> = ({ onClose, hasReviewButton, onShowTab }) => {
  const [current, setCurrent] = useState(0);
  const moving = useRef(0);

  // The hello is said on the first tab.
  useEffect(() => {
    onShowTab?.('dashboard');
    // Once, when the tour starts: `onShowTab` is a new function on every render of the dashboard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Opens the stop's tab, waits until the part it points at is drawn, then moves there. */
  const goTo = (index: number) => {
    const stop = STOPS[index];
    if (!stop) return;
    if (stop.tab) onShowTab?.(stop.tab);
    const turn = ++moving.current;
    const started = Date.now();
    const arrive = () => {
      if (turn !== moving.current) return;
      const ready = !stop.target || !!findTourTarget(stop.target);
      // A part that never shows (an empty tab, a narrow screen): Echo says it from the middle.
      if (ready || Date.now() - started > TARGET_WAIT_MS) setCurrent(index);
      else setTimeout(arrive, 50);
    };
    arrive();
  };

  const close = () => {
    moving.current++;
    onShowTab?.('dashboard');
    onClose();
  };

  const steps: TourProps['steps'] = STOPS.map((stop, index) => {
    const pointsAt = stop.target === 'review' && !hasReviewButton ? undefined : stop.target;
    return {
      // No separate headline: it is part of what Echo says.
      title: null,
      description: <EchoSays key={index} mood={stop.mood} title={stop.title} says={stop.says} />,
      // Looked up when the step is shown; with nothing to point at the step is centred.
      target: pointsAt ? () => findTourTarget(pointsAt) as HTMLElement : undefined,
      nextButtonProps: {
        children: index === 0 ? 'Show me' : index === STOPS.length - 1 ? 'Let’s go' : 'Next',
      },
      prevButtonProps: { children: 'Back' },
    };
  });

  return (
    <>
      <style>{HOP}</style>
      <Tour
        open
        current={current}
        onChange={goTo}
        onClose={close}
        onFinish={close}
        steps={steps}
        scrollIntoViewOptions={{ block: 'center' }}
      />
    </>
  );
};
